import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import ExpiringProducts from 'components/widgets/ExpiringProducts';
import DeliveriesWidget from 'components/widgets/DeliveriesWidget';
import OpenTickets from 'components/widgets/OpenTickets';
import {
  ExpiringMaintenancesWithoutTypeWidget,
  ExpiringMaintenancesQualificationWidget,
  ExpiringMaintenancesProcessValidationWidget,
  ExpiringMaintenancesElectricalWidget
} from 'components/widgets/ExpiringMaintenancesWidget';
import WaitingMaintenancesWidget from 'components/widgets/WaitingMaintenancesWidget';
import ReportsWidget from 'components/widgets/ReportsWidget';
import UnderstockProducts from 'components/widgets/UnderstockProducts';
import { canViewTechnicianCreatedTickets } from 'components/helpers/IsBottomFromTechnician';
import { isSupplier } from 'components/helpers/IsSupplier';
import { isTechnician } from 'components/helpers/IsTechnician';
import TicketToApprove from 'components/widgets/TicketToApprove';
import { Paper } from '@mui/material';
import { ReactNode } from 'react';
import DeliveryExpectedWidget from 'components/widgets/DeliveryExpectedWidget';
import LisStatusWidget from 'components/widgets/LisStatusWidget';
import PurchaseRequestsToApprove from 'components/widgets/PurchaseRequestsToApprove';
import PurchaseRequestsAutomatic from 'components/widgets/PurchaseRequestsAutomatic';
import ExpiringToday from 'components/widgets/ExpiringToday';
import ExpiringContracts from 'components/widgets/ExpiringContracts';
import NotificationWidget from 'components/widgets/NotificationWidget';
import { isDirector } from 'components/helpers/IsDirector';

const EXPIRING_MAINTENANCE_WIDGETS = [
  ExpiringMaintenancesWithoutTypeWidget,
  ExpiringMaintenancesQualificationWidget,
  ExpiringMaintenancesProcessValidationWidget,
  ExpiringMaintenancesElectricalWidget
] as const;

const widgetCardSx = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper'
} as const;

function WidgetCard({ children, minHeight }: { children: ReactNode; minHeight?: number | string }) {
  return (
    <Paper elevation={0} sx={{ ...widgetCardSx, minHeight }}>
      {children}
    </Paper>
  );
}

function ExpiringMaintenanceWidgetCards({ minHeight = 260 }: { minHeight?: number | string }) {
  return (
    <>
      {EXPIRING_MAINTENANCE_WIDGETS.map((Widget, index) => (
        <WidgetCard key={index} minHeight={minHeight}>
          <Widget />
        </WidgetCard>
      ))}
    </>
  );
}

// ------------------------------------------------------------------------------------------------ TECHNICHIAN WIDGETS
function DashboardWidgets({ showTicketApprove }: { showTicketApprove: boolean }) {
  return (
    <>
      <div
        className={`grid grid-cols-1 gap-4 ${
          showTicketApprove ? 'md:grid-cols-3' : 'md:grid-cols-2'
        }`}>
        <WidgetCard minHeight={220}>
          <DeliveriesWidget />
        </WidgetCard>
        <WidgetCard minHeight={220}>
          <OpenTickets />
        </WidgetCard>
        {showTicketApprove && (
          <WidgetCard minHeight={220}>
            <TicketToApprove />
          </WidgetCard>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ExpiringMaintenanceWidgetCards />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WidgetCard minHeight={260}>
          <ExpiringProducts />
        </WidgetCard>
        <WidgetCard minHeight={260}>
          <UnderstockProducts />
        </WidgetCard>
      </div>
    </>
  );
}

function DirectorWidgets() {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard minHeight={220}>
          <DeliveryExpectedWidget />
        </WidgetCard>
        <WidgetCard minHeight={220}>
          <WaitingMaintenancesWidget />
        </WidgetCard>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ExpiringMaintenanceWidgetCards />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <WidgetCard minHeight={260}>
          <ReportsWidget />
        </WidgetCard>
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------------------------ ALL ROLES WIDGETS
function LegacyTenantDashboard() {
  const showTicketApprove = canViewTechnicianCreatedTickets();

  return (
    <>
      <div className="w-1/6 p-5">
        <DeliveryExpectedWidget />
      </div>

      <div className="w-1/6 p-5">
        <PurchaseRequestsToApprove />
      </div>

      <div className="w-1/6 p-5">
        <PurchaseRequestsAutomatic />
      </div>

      <div className="w-1/6 p-5">
        <LisStatusWidget />
      </div>

      {showTicketApprove && (
        <div className="w-1/6 p-5">
          <TicketToApprove />
        </div>
      )}

      <div className="w-1/6 p-5">
        <OpenTickets />
      </div>

      <div className="w-full flex flex-wrap">
        <div className="w-full p-4">
          <ExpiringToday />
        </div>
      </div>

      <div className="w-full flex flex-wrap">
        {EXPIRING_MAINTENANCE_WIDGETS.map((Widget, index) => (
          <div key={index} className="w-1/4 p-4">
            <Widget />
          </div>
        ))}
        <div className="w-1/4 p-4">
          <ExpiringContracts />
        </div>
        <div className={'w-1/4 p-4'}>
          <ExpiringProducts />
        </div>
        <div className="w-1/4 p-4">
          <UnderstockProducts />
        </div>
      </div>

      <div className="w-full p-4">
        <DeliveriesWidget />
      </div>

      <div className="w-full p-4">
        <NotificationWidget />
      </div>
    </>
  );
}

export default function TenantHome() {
  const { t } = useTranslation();
  const translationPrefix = 'dashboard';
  const isDisabled = isSupplier();
  const isDirectorUser = isDirector();

  if (isTechnician()) {
    return (
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        hideBreadcrumbs={true}>
        <div className="w-full flex flex-col gap-4 p-4">
          <DashboardWidgets showTicketApprove={false} />
        </div>
      </TenantPage>
    );
  }

  if (isDirectorUser) {
    return (
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        hideBreadcrumbs={true}>
        <div className="w-full flex flex-col gap-4 p-4">
          <DirectorWidgets />
        </div>
      </TenantPage>
    );
  }

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      hideBreadcrumbs={true}>
      <div className="flex w-full flex-wrap">{!isDisabled && <LegacyTenantDashboard />}</div>
    </TenantPage>
  );
}
